"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Anchor, Compass, Clock, Utensils, Shield, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function AnchorOrbit() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const anchorHighlights = [{
    title: "Unpack once",
    detail: "Private villa estates, chartered superyachts, or connecting heritage suites serve as your permanent grounding base."
  }, {
    title: "Transient fatigue eliminated",
    detail: "No mid-trip packing cycles, fragmented hotel check-ins, or baggage handoffs between generations."
  }, {
    title: "Private family sanctuary",
    detail: "Dedicated common lounges, private pools, and shared courtyards reserved exclusively for your family's rhythm."
  }];
  const orbitHighlights = [{
    title: "Generational divergence",
    detail: "Teenagers dive coastal coves or take private surf clinics while grandparents explore historical archives at an unhurried pace."
  }, {
    title: "Zero compromise",
    detail: "No individual is forced into an activity that does not inspire them simply to keep the group intact."
  }, {
    title: "Evening convergence",
    detail: "All generational tracks naturally reunite each sunset over curated private dinners to trade the day's stories."
  }];
  return <section id="anchor-orbit-grid" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      {/* Subtle architectural background motif */}
      <div className="absolute inset-0 pointer-events-none opacity-40 flex items-center justify-center">
        <div className="w-[800px] h-[800px] rounded-full border border-border/40" />
        <div className="absolute w-[1200px] h-[1200px] rounded-full border border-border/20" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }} className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-sm bg-muted text-foreground/80 text-xs font-semibold uppercase tracking-widest border border-border">
            <Compass className="w-3.5 h-3.5 text-primary" />
            <span>Proprietary Methodology</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight mb-5">
            The dual architecture of shared legacy
          </h2>
          <p className="text-base sm:text-lg text-foreground/75 leading-relaxed font-sans">
            Multi-generational travel fails when forced into rigid uniformity. The Anchor & Orbit framework balances deep collective grounding with uncompromising individual autonomy.
          </p>
        </motion.div>

        {/* Two-Column Split Architecture Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true
      }} className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">
          {/* Column 1: The Anchor */}
          <motion.div variants={itemVariants} className="h-full">
            <Card className="h-full bg-card border border-border rounded-sm shadow-md hover:border-primary/50 transition-[border-color,box-shadow] duration-300 flex flex-col justify-between overflow-hidden">
              <div>
                {/* Visual Header Banner */}
                <div className="relative h-64 sm:h-72 w-full overflow-hidden border-b border-border">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/anchor_sanctuary_villa.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/anchor_sanctuary_villa.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/anchor_sanctuary_villa.png-wvc-sizes" alt="Private secluded luxury estate terrace overlooking the sea at golden hour" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                  <div className="absolute top-4 left-4 bg-card/90 backdrop-blur-xs px-3 py-1 rounded-sm border border-border text-xs font-semibold uppercase tracking-wider text-foreground">
                    Dimension 01
                  </div>
                  <div className="absolute bottom-4 left-6 right-6 text-white">
                    <div className="flex items-center gap-2 mb-1">
                      <Anchor className="w-5 h-5 text-white/90" />
                      <span className="text-xs uppercase tracking-widest text-white/80 font-semibold">The Sanctuary Base</span>
                    </div>
                    <h3 className="font-serif text-2xl sm:text-3xl font-semibold tracking-tight">
                      The Anchor
                    </h3>
                  </div>
                </div>

                <CardContent className="p-6 sm:p-8 space-y-6">
                  <p className="text-foreground/80 leading-relaxed font-sans text-sm sm:text-base">
                    Establish a single private stronghold—whether an estate in Tuscany, a chartered yacht along the Amalfi Coast, or connected alpine chalets. Your multi-generational party unpacks once, creating a calm emotional center where members retreat at will.
                  </p>

                  <div className="border-t border-border pt-6 space-y-4">
                    {anchorHighlights.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-3.5">
                        <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-muted flex items-center justify-center border border-border">
                          <Shield className="w-3 h-3 text-primary" />
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-foreground tracking-tight">
                            {item.title}
                          </h4>
                          <p className="text-xs sm:text-sm text-foreground/70 mt-0.5 leading-relaxed">
                            {item.detail}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </CardContent>
              </div>

              <div className="px-6 sm:px-8 pb-6 sm:pb-8 pt-2">
                <div className="bg-muted/60 p-4 rounded-sm border border-border/80 flex items-center justify-between text-xs text-foreground/75">
                  <span className="font-medium">Anchor Deliverables</span>
                  <span>Dedicated Staff &bull; Zero Logistics Stress</span>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Column 2: The Orbit */}
          <motion.div variants={itemVariants} className="h-full">
            <Card className="h-full bg-card border border-border rounded-sm shadow-md hover:border-primary/50 transition-[border-color,box-shadow] duration-300 flex flex-col justify-between overflow-hidden">
              <div>
                {/* Visual Header Banner */}
                <div className="relative h-64 sm:h-72 w-full overflow-hidden border-b border-border">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/orbit_generational_excursions.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/orbit_generational_excursions.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/orbit_generational_excursions.png-wvc-sizes" alt="Multi-generational family enjoying a private coastal excursion at golden hour" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                  <div className="absolute top-4 left-4 bg-card/90 backdrop-blur-xs px-3 py-1 rounded-sm border border-border text-xs font-semibold uppercase tracking-wider text-foreground">
                    Dimension 02
                  </div>
                  <div className="absolute bottom-4 left-6 right-6 text-white">
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="w-5 h-5 text-white/90" />
                      <span className="text-xs uppercase tracking-widest text-white/80 font-semibold">The Custom Journeys</span>
                    </div>
                    <h3 className="font-serif text-2xl sm:text-3xl font-semibold tracking-tight">
                      The Orbit
                    </h3>
                  </div>
                </div>

                <CardContent className="p-6 sm:p-8 space-y-6">
                  <p className="text-foreground/80 leading-relaxed font-sans text-sm sm:text-base">
                    Every generation experiences the destination on their own terms. While youth explore high-energy maritime sports or local artisan workshops, elders enjoy relaxed vineyard tastings and private gallery access—all orchestrated to precision.
                  </p>

                  <div className="border-t border-border pt-6 space-y-4">
                    {orbitHighlights.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-3.5">
                        <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-muted flex items-center justify-center border border-border">
                          <Clock className="w-3 h-3 text-primary" />
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-foreground tracking-tight">
                            {item.title}
                          </h4>
                          <p className="text-xs sm:text-sm text-foreground/70 mt-0.5 leading-relaxed">
                            {item.detail}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </CardContent>
              </div>

              <div className="px-6 sm:px-8 pb-6 sm:pb-8 pt-2">
                <div className="bg-muted/60 p-4 rounded-sm border border-border/80 flex items-center justify-between text-xs text-foreground/75">
                  <span className="font-medium">Orbit Synchronization</span>
                  <span>Private Chauffeurs &bull; Sunset Reunions</span>
                </div>
              </div>
            </Card>
          </motion.div>
        </motion.div>

        {/* Convergence Philosophy Bar */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2,
        ease: [0.16, 1, 0.3, 1]
      }} className="mt-12 lg:mt-16 bg-card border border-border rounded-sm p-6 sm:p-8 lg:p-10 shadow-sm">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-10">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-muted rounded-sm border border-border flex-shrink-0">
                <Utensils className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h4 className="font-serif text-xl sm:text-2xl font-medium tracking-tight text-foreground mb-1">
                  The evening reunion
                </h4>
                <p className="text-sm sm:text-base text-foreground/75 leading-relaxed max-w-2xl font-sans">
                  The day concludes at the Anchor. Shared meals prepared by private chefs provide an effortless setting to share the day&apos;s distinct journeys, reinforcing family bonds without midday tension.
                </p>
              </div>
            </div>

            <div className="flex-shrink-0 w-full sm:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-semibold px-8 py-3 rounded-xs shadow-sm">
                <Link to="/AboutDiscoveryConsultationPage">
                  <span>Plan your expedition</span>
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}